import { useState } from "react";
import { useDispatch } from "react-redux";
import Card from "../../../component/Card/Card";
import "./InsuranceInfo.scss";
import { Strings } from "../../../constant/Strings";
import EditButton from "../../../component/Buttons/EditButton/EditButton";
import { icons } from "../../../assets";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import EditInsuranceDetails from "./EditInsuranceDetails/EditInsuranceDetails";
import useLoadingIndicator from "../../../component/Loader/Loader";
import * as actions from "../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import moment from "moment";

// Onsurity's meta.responseMessage is often just a generic "invitation failed for request Id"
// wrapper — the actual actionable reason lives in meta.errors, which is either {} (no detail)
// or an array of per-record objects with a real `message` (e.g. "Member cant be added to more
// than one life plan..."). Prefer that when present, since it's what HR can actually act on.
const getOnsurityErrorMessage = (meta: any, fallback: string): string => {
  if (Array.isArray(meta?.errors) && meta.errors.length > 0) {
    const detail = meta.errors.map((e: any) => e?.message).filter(Boolean).join("; ");
    if (detail) return detail;
  }
  return meta?.responseMessage || fallback;
};

const InsuranceInfo = (props: any) => {
  const { onsurity, eId, employeeId, status, recordStatus, refreshData } = props;
  const dispatch = useDispatch();
  const { loadingComponent, showLoadingIndicator, hideLoadingIndicator } =
    useLoadingIndicator();
  const userLogin: any = localStorage.getItem("login");
  const loginId = JSON.parse(userLogin)?.id;
  const role = localStorage.getItem("role");
  const [showEditInsurance, setShowEditInsurance] = useState(false);

  const {
    onsurity_info_heading,
    onsurity_plan_heading,
    onsurity_nominee_heading,
    onsurity_dependents_heading,
    onsurity_enrollment_status,
    onsurity_health_plan,
    onsurity_life_plan,
    onsurity_policy_type,
    onsurity_refresh_status,
    onsurity_not_enrolled,
    onsurity_pending_info_flag,
    noData,
  } = Strings;

  const { edit_orange_icon } = icons;

  const enrollmentStatus = onsurity?.enrollmentStatus || onsurity_not_enrolled;
  const nominee = onsurity?.nominee;
  const dependents = onsurity?.dependents || [];
  const hasPlan = !!(onsurity?.healthPlan?.planName || onsurity?.opdPlan?.planName || onsurity?.lifePlan?.planName);
  // Mirrors the SDK's inviteMember: for Active/Pending members the main/life plans count as
  // already sent (legacy records have no invitedAt), while OPD is pending until invitedAt is set.
  const alreadyEnrolled = enrollmentStatus === "Active" || enrollmentStatus === "Pending";
  const opdNotEnrolled = !!onsurity?.opdPlan?.planName && !onsurity?.opdPlan?.invitedAt;
  const mainNotEnrolled =
    !alreadyEnrolled &&
    !!(
      (onsurity?.healthPlan?.planName && !onsurity?.healthPlan?.invitedAt) ||
      (onsurity?.lifePlan?.planName && !onsurity?.lifePlan?.invitedAt)
    );
  // Per-plan state, from Onsurity via Refresh Status (invitedAt/active) — drives the warnings.
  const planState = (plan: any, legacyCovered = false) => {
    if (!plan?.planName) return null;
    if (plan.active === true) return { label: "Active", warn: false };
    if (plan.invitedAt || legacyCovered) return { label: plan.active === false ? "Inactive" : "Enrolled", warn: false };
    return { label: "Not enrolled", warn: true };
  };
  const healthState = planState(onsurity?.healthPlan, alreadyEnrolled);
  const opdState = planState(onsurity?.opdPlan);
  const canEnroll = mainNotEnrolled || opdNotEnrolled;
  // This banner is about whether we CAN auto-enroll yet - once enrollment has actually
  // happened (Active/Pending), it's stale to keep warning "before this can be enrolled" even
  // if the nominee was never backfilled (true for most of the records brought in by the
  // production Onsurity billing import, which doesn't carry nominee data).
  const isPendingInfo = !alreadyEnrolled && (!hasPlan || !nominee?.name);

  // Enrollment is an HR action, not a self-service one — an employee viewing their own
  // Insurance tab should see status only, not be able to trigger an Onsurity API call
  // themselves.
  const isHr = !!(role && role.includes("hr"));

  // Onsurity has no correction API — once the Invite Member call has actually been made
  // (anything past "Not Enrolled"), nominee/dependent edits here no longer reach them; the
  // member has to make changes via their own Onsurity app. So editing is locked at that point
  // except for hr-admin, who may need to reconcile Spotlight's record with a change the member
  // already made on their end (e.g. added a child on the Onsurity app that we don't know about).
  const isHrAdmin = (localStorage.getItem("role") || "").includes("hr-admin");
  const onsurityEditLocked = enrollmentStatus !== onsurity_not_enrolled && !isHrAdmin;

  // Unlike the other profile tabs, Insurance editability isn't tied to the record's onboarding
  // approval stage — an employee should be able to edit their own nominee/dependents any time
  // before enrollment, active/approved status or not. It's governed purely by enrollment status.
  const editShowCondition = !onsurityEditLocked;

  const handleEditInsurance = () => {
    setShowEditInsurance(!showEditInsurance);
  };

  const handleRefreshStatus = () => {
    showLoadingIndicator();
    dispatch(
      actions.syncEmployeeInsuranceAction(eId, (res: any) => {
        hideLoadingIndicator();
        if (res?.error?.error_description) {
          showErrorToastMessage(res.error.error_description);
        } else if (res?.data?.raw?.meta?.isSuccess === false) {
          // syncStatus wraps the Onsurity response as { onsurity, raw: response } —
          // a 200-shaped ResultEntity can still carry an Onsurity-side failure here.
          showErrorToastMessage(
            getOnsurityErrorMessage(res.data.raw.meta, "Failed to refresh insurance status. Please try again.")
          );
        } else {
          showSuccessToastMessage("Insurance status refreshed!");
          refreshData?.();
        }
      })
    );
  };

  // Health and life plans are two independent documents on Onsurity's side (health: a zip of
  // health cards + plan document; life: the COI once the insurer issues it) — each gets its own
  // button and its own request, rather than one button silently opening up to two tabs at once
  // (which popup blockers can partially block, and which is unclear to the user either way).
  const handleDownloadDocuments = (category: "health" | "life") => {
    showLoadingIndicator();
    dispatch(
      actions.getEmployeeInsuranceCertificateAction(eId, category, (res: any) => {
        hideLoadingIndicator();
        if (res?.error?.error_description) {
          showErrorToastMessage(res.error.error_description);
          return;
        }
        const doc = res?.data?.[category];
        if (doc?.success && doc?.fileUrl) {
          window.open(doc.fileUrl, "_blank");
        } else {
          showErrorToastMessage(doc?.message || "Document not available yet.");
        }
      })
    );
  };

  const handleEnroll = () => {
    showLoadingIndicator();
    dispatch(
      actions.enrollEmployeeInsuranceAction(eId, (res: any) => {
        hideLoadingIndicator();
        if (res?.error?.error_description) {
          showErrorToastMessage(res.error.error_description);
        } else if (res?.data?.meta?.isSuccess === false) {
          showErrorToastMessage(
            getOnsurityErrorMessage(res.data.meta, "Enrollment failed. Please try again.")
          );
        } else {
          showSuccessToastMessage("Enrollment request sent to Onsurity!");
          refreshData?.();
        }
      })
    );
  };

  return (
    <>
      {loadingComponent()}
      <div className="insurance-container">
        <h5>{onsurity_info_heading}</h5>

        {isPendingInfo && (
          <div className="pending-info-banner">{onsurity_pending_info_flag}</div>
        )}

        <div className="card-div-container">
          <Card>
            <div className="edit-icon-wrapper">
              <h5>{onsurity_plan_heading}</h5>
              {editShowCondition && (
                <EditButton
                  title="Edit"
                  icon={edit_orange_icon}
                  onClick={handleEditInsurance}
                />
              )}
            </div>
            <div className="card-content-container">
              <div className="card-content">
                <div className="card-content-title">{onsurity_enrollment_status}</div>
                <div className="card-content-data">
                  <span className={`status-pill status-${enrollmentStatus.toLowerCase().replace(/\s+/g, "-")}`}>
                    {enrollmentStatus}
                  </span>
                </div>
              </div>
              <div className="card-content">
                <div className="card-content-title">{onsurity_health_plan}</div>
                <div className="card-content-data">
                  {onsurity?.healthPlan?.planName || "-"}
                  {healthState && (
                    <span className={healthState.warn ? "text-danger" : "text-muted"}> ({healthState.label})</span>
                  )}
                </div>
              </div>
              <div className="card-content">
                <div className="card-content-title">OPD Plan</div>
                <div className="card-content-data">
                  {onsurity?.opdPlan?.planName || "-"}
                  {opdState && (
                    <span className={opdState.warn ? "text-danger" : "text-muted"}>
                      {" "}({opdState.label}{opdState.warn ? " — click Enroll OPD" : ""})
                    </span>
                  )}
                </div>
              </div>
              <div className="card-content">
                <div className="card-content-title">{onsurity_life_plan}</div>
                <div className="card-content-data">
                  {onsurity?.lifePlan?.planName || "-"}
                </div>
              </div>
              <div className="card-content">
                <div className="card-content-title">{onsurity_policy_type}</div>
                <div className="card-content-data">
                  {onsurity?.healthPlan?.policyType ||
                    onsurity?.lifePlan?.policyType ||
                    "-"}
                </div>
              </div>
            </div>

            <div className="action-btn-wrapper">
              {!isPendingInfo &&
                isHr &&
                status !== "Inactive" &&
                canEnroll && (
                  <button
                    className="btn add-submit-button buttonsize"
                    onClick={handleEnroll}
                  >
                    {mainNotEnrolled ? "Enroll" : "Enroll OPD"}
                  </button>
                )}
              {enrollmentStatus === "Active" && onsurity?.healthPlan?.planName && (
                <button
                  className="btn add-submit-button buttonsize"
                  onClick={() => handleDownloadDocuments("health")}
                >
                  Download Health Documents
                </button>
              )}
              {enrollmentStatus === "Active" && onsurity?.lifePlan?.planName && (
                <button
                  className="btn add-submit-button buttonsize"
                  onClick={() => handleDownloadDocuments("life")}
                >
                  Download Life Documents
                </button>
              )}
              <button
                className="btn cancel-btn buttonsize"
                onClick={handleRefreshStatus}
              >
                {onsurity_refresh_status}
              </button>
            </div>
          </Card>
        </div>

        <div className="card-div-container">
          <Card>
            <div className="edit-icon-wrapper">
              <h5>{onsurity_nominee_heading}</h5>
            </div>
            {nominee?.name ? (
              <div className="card-content-container">
                <div className="card-content">
                  <div className="card-content-title">Name</div>
                  <div className="card-content-data">{nominee.name}</div>
                </div>
                <div className="card-content">
                  <div className="card-content-title">Date of Birth</div>
                  <div className="card-content-data">
                    {nominee.dob ? moment(nominee.dob).format("DD/MMM/YYYY") : "-"}
                  </div>
                </div>
                <div className="card-content">
                  <div className="card-content-title">Relation</div>
                  <div className="card-content-data">{nominee.relation}</div>
                </div>
              </div>
            ) : (
              <p>{noData}</p>
            )}
          </Card>
        </div>

        <div className="card-div-container">
          <Card>
            <div className="edit-icon-wrapper">
              <h5>{onsurity_dependents_heading}</h5>
            </div>
            {dependents.length > 0 ? (
              dependents.map((dep: any, index: number) => (
                <div className="dependent-row" key={index}>
                  <div className="card-content">
                    <div className="card-content-title">Name</div>
                    <div className="card-content-data">{dep.name}</div>
                  </div>
                  <div className="card-content">
                    <div className="card-content-title">Gender</div>
                    <div className="card-content-data">{dep.gender}</div>
                  </div>
                  <div className="card-content">
                    <div className="card-content-title">Date of Birth</div>
                    <div className="card-content-data">
                      {dep.dob ? moment(dep.dob).format("DD/MMM/YYYY") : "-"}
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="card-content-title">Relation</div>
                    <div className="card-content-data">{dep.relation}</div>
                  </div>
                </div>
              ))
            ) : (
              <p>{noData}</p>
            )}
          </Card>
        </div>
      </div>

      <ModalComponents
        show={showEditInsurance}
        onHide={handleEditInsurance}
        title="Edit Insurance Details"
        modalBody={
          <EditInsuranceDetails
            onsurity={onsurity}
            employeeId={eId}
            eId={eId}
            handleEditInsurance={handleEditInsurance}
            refreshData={refreshData}
            isHr={isHr}
          />
        }
        className="modal-lg"
      />
    </>
  );
};

export default InsuranceInfo;
